import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js' // eslint-disable-line no-unused-vars

import { httpOk, POST } from 'helpers/api'
import { getSocketConfig } from 'helpers/env'

/**
 * Подключение админки к каналу точки: живая лента команд, которые облако шлёт
 * кассе, и выполнение запросов на её локальной базе.
 *
 * Зачем канал точки, а не отчёты по облачной базе: облако знает только то, что
 * ОНО отправило. Вопрос «дошёл ли заказ агрегатора до кассы» — про другую
 * сторону провода, и ответ на него есть только в локальной базе кассы.
 * Поэтому монитор показывает обе стороны рядом: слева отправленная команда,
 * справа то, что после неё реально лежит в базе кассы.
 */

/** Дольше касса не отвечает никогда: запрос идёт по сокету и выполняется локально. */
const QUERY_TIMEOUT_MS = 15000

const MAX_EVENTS = 300

/** Команды кассе, которые шлёт сам монитор. В ленте это только шум. */
const SILENT_EVENTS = ['sync.pos.sqlquery', 'sync.pos.sqlresult', 'sqlQuery', 'sqlQueryResult']

const SYNC_EVENTS = [
	'.sync.cheque.create',
	'.sync.cheque.update',
	'.sync.cheque.close',
	'.sync.cheque.delete',
	'.sync.cheque.print',
	'.sync.cheque.status',
	'.sync.cheque.address',
	'.sync.cheque.courier.status',
	'.sync.cheque.payment',
	'.sync.cheque.courier.assign',
	'.sync.cheque.courier.remove',
	'.sync.pos.request',
	'.sync.pos.update',
	'.sync.reservation.create',
	'.sync.reservation.update',
	'.sync.qr.table',
	'.sync.qr.order',
]

let eventSeq = 0

/**
 * basePath — раздел, из которого работает монитор: '/admin' или '/partner'.
 * Ручки одинаковые, различаются только префиксом и правами на той стороне.
 */
export default function usePosMonitor(basePath = '/admin') {
	const reduxAccount = useSelector(state => state.account)

	const echoRef = useRef(null)
	const posIdRef = useRef(null)
	const loginRef = useRef(null)

	/** request_id → { resolve, timer }. Ответ приходит асинхронно, отдельным событием. */
	const pendingRef = useRef(new Map())

	/**
	 * Запросы отправляются строго по одному.
	 *
	 * Старая касса номер запроса не возвращает, и сервер сопоставляет ответ с
	 * запросом по порядку отправки. Два запроса в воздухе одновременно — и
	 * порядок ответов уже не гарантирован: в чек попали бы позиции соседнего.
	 */
	const queueRef = useRef(Promise.resolve())

	const [status, setStatus] = useState('idle')
	const [events, setEvents] = useState([])
	const [members, setMembers] = useState([])

	const pushEvent = useCallback((event) => {
		eventSeq += 1

		setEvents((prev) => {
			const next = [{ ...event, key: eventSeq, at: Date.now() }, ...prev]
			return next.length > MAX_EVENTS ? next.slice(0, MAX_EVENTS) : next
		})
	}, [])

	const clearEvents = useCallback(() => setEvents([]), [])

	const resolvePending = useCallback((result) => {
		const requestId = result?.request_id
		if (!requestId) return

		const entry = pendingRef.current.get(requestId)
		// Ответа никто не ждёт: дубль из второго канала (одно и то же событие
		// едет и как Connection, и как sync.pos.sqlresult) либо опоздавший.
		if (!entry) return

		pendingRef.current.delete(requestId)
		clearTimeout(entry.timer)
		entry.resolve({
			rows: Array.isArray(result.rows) ? result.rows : [],
			count: result.count ?? 0,
			error: result.error || null,
			took_ms: result.took_ms ?? null,
			query: result.query || null,
		})
	}, [])

	const disconnect = useCallback(() => {
		const echo = echoRef.current

		for (const entry of pendingRef.current.values()) {
			clearTimeout(entry.timer)
			entry.resolve({ rows: [], error: 'Соединение закрыто', took_ms: null })
		}
		pendingRef.current.clear()

		if (echo) {
			try {
				if (posIdRef.current) echo.leave(`pos.${posIdRef.current}`)
				if (loginRef.current) echo.leave(`presence-call-center-${loginRef.current}`)
				echo.disconnect()
			} catch (error) {
				console.log('monitor disconnect', error)
			}
		}

		echoRef.current = null
		posIdRef.current = null
		loginRef.current = null
		setMembers([])
		setStatus('idle')
	}, [])

	const connect = useCallback((posId, login) => {
		if (echoRef.current) disconnect()
		if (!posId) return

		const config = getSocketConfig()
		// Токен админа, а не владельца точки: подписку админа на чужой канал
		// разрешает ChannelAuthService, и чужие токены светить незачем.
		const token = reduxAccount?.token || localStorage.getItem('token')

		setStatus('connecting')

		let echo
		try {
			echo = new Echo({
				broadcaster: 'pusher',
				key: 'mison_key',
				wsHost: config.wsHost,
				wsPort: config.wsPort,
				wssPort: config.wsPort,
				enabledTransports: ['ws', 'wss'],
				disableStats: true,
				encrypted: config.isProd,
				useTLS: config.isProd,
				forceTLS: config.isProd,
				authEndpoint: config.authEndpoint,
				auth: {
					headers: {
						'Authorization': `Bearer ${token}`,
					},
				},
			})
		} catch (error) {
			console.log('monitor echo', error)
			setStatus('error')
			return
		}

		echoRef.current = echo
		posIdRef.current = posId
		loginRef.current = login

		echo.connector.pusher.connection.bind('connected', () => setStatus('connected'))
		echo.connector.pusher.connection.bind('disconnected', () => setStatus('disconnected'))
		echo.connector.pusher.connection.bind('unavailable', () => setStatus('error'))
		echo.connector.pusher.connection.bind('failed', () => setStatus('error'))

		const channel = echo.private(`pos.${posId}`)

		// Новые имена команд. Касса 250+ слушает ровно их же — то есть монитор
		// видит буквально то, что получает касса, а не пересказ из облака.
		SYNC_EVENTS.forEach((name) => {
			channel.listen(name, (payload) => {
				pushEvent({
					name: name.replace(/^\./, ''),
					protocol: 'sync',
					data: payload?.data ?? payload,
				})
			})
		})

		channel.listen('.sync.pos.sqlresult', (payload) => resolvePending(payload?.data))

		// Legacy-протокол: старая касса подписана только на него. Держим оба,
		// чтобы монитор одинаково работал и со старым парком.
		channel.listen('Connection', (payload) => {
			const type = payload?.type

			if (type === 'sqlQueryResult') {
				resolvePending(payload?.data)
				return
			}

			if (SILENT_EVENTS.includes(type)) return

			pushEvent({
				name: type || 'Connection',
				protocol: 'legacy',
				data: payload?.data,
			})
		})

		if (login) {
			// Онлайн точки: касса сама джойнится в этот канал при запуске.
			echo.join(`presence-call-center-${login}`)
				.here((users) => setMembers(users || []))
				.joining((user) => setMembers((prev) => [...prev, user]))
				.leaving((user) => setMembers((prev) => prev.filter((item) => item.id !== user.id)))
				.error((error) => console.log('presence error', error))
		}
	}, [disconnect, pushEvent, resolvePending, reduxAccount])

	/**
	 * Выполнить запрос на кассе. Промис не отклоняется: несостоявшийся ответ —
	 * это тоже результат, и показать его надо там же, где обычный.
	 */
	const sendQuery = useCallback(async (query, type = 'select') => {
		const posId = posIdRef.current

		if (!posId || !echoRef.current) {
			return { rows: [], error: 'Нет подключения к точке', took_ms: null }
		}

		const response = await POST(`${basePath}/sql-query`,
			{ 'pos_id': posId, 'type': type, 'query': query },
			{ notification: false })

		if (!httpOk(response)) {
			return {
				rows: [],
				error: response?.data?.message || 'Запрос не удалось отправить',
				took_ms: null,
			}
		}

		const requestId = response.data?.request_id
		if (!requestId) {
			return { rows: [], error: 'Сервер не вернул номер запроса', took_ms: null }
		}

		return new Promise((resolve) => {
			const timer = setTimeout(() => {
				pendingRef.current.delete(requestId)

				// Ответа не будет — надо убрать запрос из серверной очереди.
				// Иначе следующий ответ кассы заберёт этот, «протухший» запрос,
				// и все дальнейшие ответы поедут на один со сдвигом.
				POST(`${basePath}/sql/reset`, { 'pos_id': posId }, { notification: false })

				resolve({
					rows: [],
					error: `Касса не ответила за ${QUERY_TIMEOUT_MS / 1000} с`,
					took_ms: QUERY_TIMEOUT_MS,
				})
			}, QUERY_TIMEOUT_MS)

			pendingRef.current.set(requestId, { resolve, timer })
		})
	}, [basePath])

	/** Очередь: следующий запрос уходит только после ответа на предыдущий. */
	const query = useCallback((sql, type = 'select') => {
		const run = () => sendQuery(sql, type)
		const next = queueRef.current.then(run, run)

		queueRef.current = next.catch(() => { })

		return next
	}, [sendQuery])

	useEffect(() => disconnect, [disconnect])

	const posOnline = members.some((member) => Number(member?.pos_id) === Number(posIdRef.current))

	const socketConfig = getSocketConfig()

	return {
		status,
		wsHost: `${socketConfig.wsHost}:${socketConfig.wsPort}`,
		connected: status === 'connected',
		posOnline,
		members,
		events,
		clearEvents,
		connect,
		disconnect,
		query,
	}
}
